import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiTv, FiUsers, FiServer, FiShoppingBag, FiFileText, FiShield,
  FiZap, FiGlobe, FiCheckCircle, FiDollarSign, FiLayers, FiRadio
} = FiIcons;

const AvatarsTab = () => {
  const { t } = useLanguage();
  const [activeVenueArea, setActiveVenueArea] = useState('Lounge');

  const features = [
    {
      icon: FiTv,
      title: t('solutionsPage.avatars.features.f1Title') || "The Ultimate Broadcast Hub",
      description: t('solutionsPage.avatars.features.f1Desc') || "Supports YouTube, Vimeo, Twitch, or HLS CDN. Seamlessly integrate high-quality live streams with deep virtual interaction."
    },
    {
      icon: FiUsers,
      title: t('solutionsPage.avatars.features.f2Title') || "Virtual Networking Lounge",
      description: t('solutionsPage.avatars.features.f2Desc') || "A professional yet casual space for mixers. Includes profile creation, discussion groups, and private video calls."
    },
    {
      icon: FiServer,
      title: t('solutionsPage.avatars.features.f3Title') || "Infrastructure You Control",
      description: t('solutionsPage.avatars.features.f3Desc') || "Ultra low-cost video conferencing. Route calls through your own AWS Chime account for massive cost savings."
    },
    {
      icon: FiShoppingBag,
      title: t('solutionsPage.avatars.features.f4Title') || "Exhibitor Power-Ups",
      description: t('solutionsPage.avatars.features.f4Desc') || "Customizable booths for sponsors to showcase products via video and materials with integrated lead capture."
    },
    {
      icon: FiFileText,
      title: t('solutionsPage.avatars.features.f5Title') || "Dynamic Content Delivery",
      description: t('solutionsPage.avatars.features.f5Desc') || "Go beyond booths. Use flexible Pages to deliver knowledge bases or course content directly within your world."
    },
    {
      icon: FiShield,
      title: t('solutionsPage.avatars.features.f6Title') || "Collaborative Hosting",
      description: t('solutionsPage.avatars.features.f6Desc') || "Scale your management. Easily add co-hosts and moderators to help navigate schedules and manage Q&A."
    },
    {
      icon: FiZap,
      title: t('solutionsPage.avatars.features.f7Title') || "Automated Ecosystem",
      description: t('solutionsPage.avatars.features.f7Desc') || "Connect your entire stack. Native support for Zapier, Pabbly, and Webhooks for automated registrations."
    },
    {
      icon: FiGlobe,
      title: t('solutionsPage.avatars.features.f8Title') || "White-Label Brand Mastery",
      description: t('solutionsPage.avatars.features.f8Desc') || "Your brand, your domain. Full CNAME support ensures your event lives on your territory for a professional feel."
    }
  ];

  const venueAreas = [
    { id: 'Stage', name: 'Main Broadcast Stage', desc: 'YouTube, Vimeo & HLS 4K live stream with stage chat', badge: 'Live Stream' },
    { id: 'Lounge', name: 'Networking Lounge', desc: 'Spatial video mixer tables, 1-on-1 calls, group chats', badge: 'Interactive' },
    { id: 'Booths', name: 'Sponsor Expo Hall', desc: 'Interactive partner booths with video demos & lead capture', badge: 'Monetization' },
  ];

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-blue-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiGlobe} className="w-3.5 h-3.5 text-blue-600 animate-pulse" />
          <span>{t('solutionsPage.avatars.badge') || "Immersive Virtual Venues"}</span>
          <span className="bg-emerald-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded shadow-xs">{t('solutionsPage.avatars.savingsBadge') || "85% SAVINGS"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.avatars.title') || "Immersive Virtual Venues with Zero Overhead"}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.avatars.subtitle') || "Immersive virtual venues. Host high-capacity expos and networking events at 85% lower costs."}
        </p>
      </div>

      {/* Interactive Virtual Venue Simulator */}
      <div className="glass-panel rounded-[28px] p-6 md:p-8 border border-white/80 shadow-xl shadow-slate-200/40 inner-glow relative overflow-hidden space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-slate-200/70">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{t('solutionsPage.avatars.floorplanTitle') || "Avatars Virtual Venue Floorplan"}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('solutionsPage.avatars.floorplanSubtitle') || "Switch between venue zones to see live participant interaction spaces."}</p>
          </div>

          {/* Area Selector */}
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100/90 border border-slate-200/80 text-xs font-semibold">
            {venueAreas.map((area) => (
              <button
                key={area.id}
                onClick={() => setActiveVenueArea(area.id)}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  activeVenueArea === area.id
                    ? 'bg-white text-blue-600 shadow-sm font-bold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {area.name}
              </button>
            ))}
          </div>
        </div>

        {/* Venue Layout Representation */}
        <div className="grid lg:grid-cols-12 gap-6 items-center">
          {/* Visual Canvas (8 Cols) */}
          <div className="lg:col-span-8 p-6 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 text-white border border-slate-800 shadow-xl relative overflow-hidden min-h-[300px] flex flex-col justify-between">
            <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" />
            
            <div className="flex items-center justify-between z-10">
              <span className="text-xs font-mono px-2.5 py-1 rounded-full bg-white/10 text-blue-300 border border-white/10">
                {t('solutionsPage.avatars.activeZone') || "Active Zone"}: {activeVenueArea}
              </span>
              <span className="text-xs text-emerald-400 font-mono font-bold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                {t('solutionsPage.avatars.awsNominal') || "AWS Chime BYOC Nominal"}
              </span>
            </div>

            {/* Virtual Zone Content */}
            <div className="py-8 z-10">
              {activeVenueArea === 'Stage' && (
                <div className="text-center space-y-3 max-w-md mx-auto">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-400">
                    <SafeIcon icon={FiTv} className="w-7 h-7" />
                  </div>
                  <h4 className="text-base font-bold">{t('solutionsPage.avatars.stageTitle') || "Ultra-HD Stage Broadcast"}</h4>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {t('solutionsPage.avatars.stageDesc') || "Synced with YouTube, Vimeo, Twitch, or private RTMP stream with 4K resolution and live stage chat."}
                  </p>
                </div>
              )}

              {activeVenueArea === 'Lounge' && (
                <div className="space-y-4 max-w-lg mx-auto">
                  <div className="text-center space-y-1">
                    <h4 className="text-base font-bold">{t('solutionsPage.avatars.loungeTitle') || "Spatial Networking Lounge"}</h4>
                    <p className="text-xs text-slate-300">{t('solutionsPage.avatars.loungeDesc') || "Click a table to jump into a private video mixer group."}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {['VIP Founders Table', 'AI Tech Discussion', 'Open Pitch Lounge'].map((table, i) => (
                      <div key={i} className="p-3 rounded-xl bg-white/10 border border-white/10 text-center hover:bg-white/20 transition-all cursor-pointer">
                        <div className="text-xs font-bold text-white">{table}</div>
                        <div className="text-[10px] text-blue-300 mt-1 font-mono">4 / 6 Seats</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeVenueArea === 'Booths' && (
                <div className="space-y-4 max-w-lg mx-auto">
                  <div className="text-center space-y-1">
                    <h4 className="text-base font-bold">{t('solutionsPage.avatars.boothsTitle') || "Interactive Exhibitor Expo"}</h4>
                    <p className="text-xs text-slate-300">{t('solutionsPage.avatars.boothsDesc') || "Sponsor power-ups with video previews and 1-click lead capture."}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3.5 rounded-xl bg-white/10 border border-white/10 space-y-1">
                      <span className="text-[10px] uppercase font-bold text-amber-300 font-mono">Platinum Sponsor</span>
                      <div className="text-xs font-bold text-white">Acme Cloud Systems</div>
                      <p className="text-[10px] text-slate-300">Live Video Demo • 120 Leads Captured</p>
                    </div>
                    <div className="p-3.5 rounded-xl bg-white/10 border border-white/10 space-y-1">
                      <span className="text-[10px] uppercase font-bold text-indigo-300 font-mono">Gold Partner</span>
                      <div className="text-xs font-bold text-white">NextGen Analytics</div>
                      <p className="text-[10px] text-slate-300">Download Whitepaper • 85 Leads</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Status */}
            <div className="flex items-center justify-between text-xs text-slate-400 z-10 pt-2 border-t border-slate-800">
              <span>{t('solutionsPage.avatars.cnameTitle') || "White-Label Custom CNAME Domain"}</span>
              <span className="text-white font-mono font-bold">{t('solutionsPage.avatars.cnameDesc') || "100% Brand Control"}</span>
            </div>
          </div>

          {/* Infrastructure Cost Breakdown (4 Cols) */}
          <div className="lg:col-span-4 space-y-4">
            <div className="p-5 rounded-2xl bg-white/80 border border-slate-200/80 shadow-sm space-y-3">
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                {t('solutionsPage.avatars.byocBadge') || "BYOC Cost Advantage"}
              </span>
              <h4 className="text-sm font-bold text-slate-900">{t('solutionsPage.avatars.byocHeading') || "85% Lower Hosting Bills"}</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                {t('solutionsPage.avatars.byocText') || "Traditional virtual venue platforms charge tens of thousands in attendee minutes. By routing through your own AWS Chime account, you pay direct wholesale cloud rates."}
              </p>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-slate-700">
                <span>{t('solutionsPage.avatars.traditionalCost') || "Traditional SaaS:"}</span>
                <span className="line-through text-slate-400">$12,500</span>
              </div>
              <div className="flex items-center justify-between text-xs font-bold text-emerald-600">
                <span>{t('solutionsPage.avatars.avatarsCost') || "Avatars + AWS Chime:"}</span>
                <span className="text-base font-extrabold text-emerald-600">$1,850</span>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-blue-50/70 border border-blue-100 space-y-1.5">
              <div className="flex items-center gap-1.5 font-bold text-xs text-blue-900">
                <SafeIcon icon={FiZap} className="w-3.5 h-3.5 text-blue-600" />
                <span>{t('solutionsPage.avatars.zapierTitle') || "Zapier & Webhooks Ready"}</span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                {t('solutionsPage.avatars.zapierDesc') || "Auto-register attendees from Eventbrite, HubSpot, or custom portals via native webhooks."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* The 8 Core Features Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {features.map((feat, idx) => (
          <motion.div
            key={idx}
            whileHover={{ y: -3 }}
            className="glass-card rounded-2xl p-5 border border-white/80 shadow-sm shadow-slate-100/60 hover:shadow-lg hover:border-blue-200/80 transition-all flex flex-col justify-between"
          >
            <div className="space-y-2.5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-600/20 border border-blue-200/60 flex items-center justify-center text-blue-600 shadow-sm">
                <SafeIcon icon={feat.icon} className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 tracking-tight">{feat.title}</h3>
              <p className="text-xs text-slate-600 leading-relaxed font-normal">{feat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AvatarsTab;
